<template>
  <div class="page-listing-search">
    <ListingSearchResults :company="company" />
  </div>
</template>

<script lang="ts" setup>
import {Company} from '~/@types/company';
import {ApiReturn} from '~/@types/common';
import {useListingStore} from '~/store/listing';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const route = useRoute();
const config = useRuntimeConfig();
const company: Ref<Company | null> = ref(null);
const listingStore = useListingStore();
const {$listing} = useNuxtApp();
const hasMounted = ref(false);
const {$metaPixelTrackEvent}: any = useNuxtApp();

// DATA Fetching
//----------------------------------------------------------------------------------------------------------------------
const {data} = await useFetch(() => `/public/companies/show/${route.params.slug}`,
  { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
);
if (data.value) {
  if ((data.value as ApiReturn).code === 200 && (data.value as ApiReturn).data) {
    // Data is returned
    company.value = (data.value as ApiReturn).data as Company;
    listingStore.setCompanySlug(company.value.slug);
  } else if ((data.value as ApiReturn).code === 404) {
    // No data is returned. Page not found
    throw createError({statusCode: 404, message: 'Company not found', fatal: true});
  } else {
    // Something went wrong
    throw createError({statusCode: 500, message: 'Something went wrong', fatal: true});
  }
}

/**
 * @_SERVER_PREFETCH Hook
 */
onServerPrefetch(async () => {
  if (company.value) {
    listingStore.setCompanySlug(company.value.slug);
  }
  await setListingDataResponse();
});

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const title = company.value ? `${company.value.displayName}'s Listings | New Homes - Explore ${company.value.displayName}'s House & Land Packages, Designs, Showhomes and more` : 'Builder Listings | New Homes - Explore House & Land Packages, Designs, Showhomes and more';
const description = `View all listings from ${company.value ? company.value.displayName : 'this builder'} on New Homes. Explore house and land packages, designs, showhomes, and more.`;
const seoImageUrl = company.value?.bannerUrl ?? 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg';
useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage: seoImageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: seoImageUrl,
});

/**
 * @_MOUNTED Hook
 */
onMounted(async () => {
  nextTick(() => {
    hasMounted.value = true;
  });

  listingStore.setIsLoading(true);
  if (listingStore.listingData.found === 0 || ListingSearchQueryHandler.isCurrentParamsDifferentToFetchedParams()) {
    setListingDataResponse();
  } else {
    listingStore.setIsLoading(false);
  }
});

/**
 * @_WATCH route.query
 */
watch(() => route.query, async () => {
  if (ListingSearchQueryHandler.isCurrentParamsDifferentToFetchedParams()) {
    await setListingDataResponse();
  }
});

function trackMetaEvent () {
  if (!company.value) {
    return;
  }

  // Track meta event
  $metaPixelTrackEvent('ViewContent', {
    content_name: company.value.name,
    content_category: company.value?.profileType ?? 'Builder',
    content_ids: [company.value.did],
    content_type: 'Company Listing',
  });
}

/**
 * Set the listing data
 */
async function setListingDataResponse () {
  listingStore.setIsLoading(true);
  listingStore.setLastFetchedParams(getCurrentParamsJsonString());
  const listingResultResponse = await $listing.search();
  listingStore.setListingData(listingResultResponse);
  listingStore.setIsLoading(false);
}

/**
 * Get the last fetched params
 */
function getCurrentParamsJsonString () {
  return JSON.stringify(route.query) + JSON.stringify(listingStore.companySlug);
}
</script>
